//src/pages/FaqBot.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "../axiosConfig";
import {
  Bot,
  CheckCircle2,
  Pencil,
  Plus,
  Save,
  Search,
  Trash2,
} from "lucide-react";

import "./FaqBot.css";

function FaqBot() {
  const [faqs, setFaqs] = useState([]);
  const [form, setForm] = useState({ palavra_chave: "", resposta: "" });
  const [editandoId, setEditandoId] = useState(null);
  const [mensagem, setMensagem] = useState("");
  const [busca, setBusca] = useState("");

  const carregarFaqs = async () => {
    try {
      const { data } = await axios.get("/faq-bot");
      setFaqs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erro ao carregar FAQs:", err);
    }
  };

  useEffect(() => {
    carregarFaqs();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const salvar = async () => {
    if (!form.palavra_chave || !form.resposta) return;

    try {
      if (editandoId) {
        await axios.put(`/faq-bot/${editandoId}`, { ...form, ativo: true });
        setMensagem("Resposta atualizada!");
      } else {
        await axios.post("/faq-bot", form);
        setMensagem("Resposta cadastrada!");
      }

      setForm({ palavra_chave: "", resposta: "" });
      setEditandoId(null);
      carregarFaqs();

      setTimeout(() => setMensagem(""), 2500);
    } catch (err) {
      console.error("Erro ao salvar:", err);
    }
  };

  const editar = (faq) => {
    setForm({
      palavra_chave: faq.palavra_chave,
      resposta: faq.resposta,
    });
    setEditandoId(faq.id);
  };

  const excluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta entrada?")) return;

    try {
      await axios.delete(`/faq-bot/${id}`);
      carregarFaqs();
    } catch (err) {
      console.error("Erro ao excluir:", err);
    }
  };

  const cancelarEdicao = () => {
    setForm({ palavra_chave: "", resposta: "" });
    setEditandoId(null);
  };

  const faqsFiltradas = useMemo(() => {
    return faqs.filter((f) =>
      (f.palavra_chave || "").toLowerCase().includes(busca.toLowerCase())
    );
  }, [faqs, busca]);

  return (
    <div className="faqBotAdmin-page">
      <div className="faqBotAdmin-topbar">
        <div>
          <span className="faqBotAdmin-kicker">Automação de atendimento</span>
          <h2>FAQ do Bot Tikpag</h2>
          <p>
            Gerencie palavras-chave e respostas automáticas usadas pelo bot no
            atendimento aos clientes.
          </p>
        </div>
      </div>

      <div className="faqBotAdmin-cards">
        <div className="faqBotAdmin-card">
          <span>Total de respostas</span>
          <strong>{faqs.length.toLocaleString("pt-BR")}</strong>
        </div>

        <div className="faqBotAdmin-card active">
          <span>Resultados filtrados</span>
          <strong>{faqsFiltradas.length.toLocaleString("pt-BR")}</strong>
        </div>
      </div>

      {mensagem && (
        <div className="faqBotAdmin-success">
          <CheckCircle2 size={16} />
          {mensagem}
        </div>
      )}

      <div className="faqBotAdmin-formCard">
        <div className="faqBotAdmin-formHeader">
          <div className="faqBotAdmin-icon">
            {editandoId ? <Pencil size={20} /> : <Plus size={20} />}
          </div>

          <div>
            <h3>{editandoId ? "Editar resposta" : "Nova resposta"}</h3>
            <span>
              Cadastre uma palavra-chave e a resposta que o bot deverá retornar.
            </span>
          </div>
        </div>

        <div className="faqBotAdmin-form">
          <div className="faqBotAdmin-field">
            <label>Palavra-chave</label>
            <input
              type="text"
              name="palavra_chave"
              placeholder="Ex: frete"
              value={form.palavra_chave}
              onChange={handleChange}
            />
          </div>

          <div className="faqBotAdmin-field resposta">
            <label>Resposta do bot</label>
            <input
              type="text"
              name="resposta"
              placeholder="Digite a resposta automática"
              value={form.resposta}
              onChange={handleChange}
            />
          </div>

          <button
            type="button"
            className="faqBotAdmin-btn primary"
            onClick={salvar}
          >
            <Save size={16} />
            {editandoId ? "Atualizar" : "Cadastrar"}
          </button>

          {editandoId && (
            <button
              type="button"
              className="faqBotAdmin-btn secondary"
              onClick={cancelarEdicao}
            >
              Cancelar
            </button>
          )}
        </div>
      </div>

      <div className="faqBotAdmin-searchCard">
        <div className="faqBotAdmin-search">
          <Search size={15} />
          <input
            type="text"
            placeholder="Buscar palavra-chave..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
      </div>

      <div className="faqBotAdmin-tableCard">
        <div className="faqBotAdmin-tableHeader">
          <div>
            <h3>Respostas cadastradas</h3>
            <span>{faqsFiltradas.length.toLocaleString("pt-BR")} resultado(s)</span>
          </div>

          <Bot size={18} />
        </div>

        <div className="faqBotAdmin-tableWrapper">
          <table className="faqBotAdmin-table">
            <thead>
              <tr>
                <th>Palavra-chave</th>
                <th>Resposta</th>
                <th>Ações</th>
              </tr>
            </thead>

            <tbody>
              {faqsFiltradas.length === 0 ? (
                <tr>
                  <td colSpan="3" className="faqBotAdmin-empty">
                    Nenhuma resposta encontrada.
                  </td>
                </tr>
              ) : (
                faqsFiltradas.map((faq) => (
                  <tr key={faq.id}>
                    <td>
                      <span className="faqBotAdmin-keyword">
                        {faq.palavra_chave}
                      </span>
                    </td>

                    <td>
                      <div className="faqBotAdmin-answer">
                        {faq.resposta}
                      </div>
                    </td>

                    <td>
                      <div className="faqBotAdmin-actions">
                        <button
                          type="button"
                          className="faqBotAdmin-actionBtn edit"
                          onClick={() => editar(faq)}
                        >
                          <Pencil size={14} />
                          Editar
                        </button>

                        <button
                          type="button"
                          className="faqBotAdmin-actionBtn delete"
                          onClick={() => excluir(faq.id)}
                        >
                          <Trash2 size={14} />
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default FaqBot;